import React from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
// Die Klassen `when-dropdown__…` stehen in WhenDropdown.scss — auch, wenn nur Deadline diesen Teil nutzt.
import './WhenDropdown.scss'

interface WhenDropdownFooterProps {
  placement: 'actionbar' | 'badge' | 'sheet' | 'inline'
  hasSelection: boolean
  /** Whether the task already has a date (controls the Trash button in `sheet`). */
  hasExistingDate?: boolean
  onConfirm: () => void
  onRemove?: () => void
  onClose: () => void
}

export const WhenDropdownFooter: React.FC<WhenDropdownFooterProps> = ({
  placement,
  hasSelection,
  hasExistingDate = false,
  onConfirm,
  onRemove,
  onClose,
}) => {
  const { t } = useTranslation()

  // Sheet footer: permanently visible + sticky. Confirm is always present and
  // active — it saves the selection, or simply closes when nothing is selected.
  // Trash (remove) only shows when the task already has a date — right next to
  // Confirm, like every footer (Sven, 10.10.2026).
  if (placement === 'sheet') {
    return (
      <div className="when-dropdown__footer when-dropdown__footer--sheet">
        <div className="context-menu-content__separator" />
        <div className="when-dropdown__footer-actions">
          {onRemove && hasExistingDate && (
            <Button variant="outline" icon={<Trash2 size={16} />} onClick={() => { onClose(); setTimeout(() => onRemove(), 0) }}>
              {t('taskCreation.whenRemove')}
            </Button>
          )}
          <Button
            variant="primary"
            shortcut={{ kind: 'return' }}
            onClick={() => { if (hasSelection) onConfirm(); else onClose() }}
          >
            {hasSelection ? t('taskCreation.whenConfirm') : t('taskCreation.whenClose')}
          </Button>
        </div>
      </div>
    )
  }

  /*
    **In den Aufgaben-Details bestätigt nichts** (ADR 0026): Der Kasten
    schreibt dort jede Änderung sofort. Übrig bleibt der Rückweg — und der nur,
    wenn der Aufrufer den Griff reicht. Er reicht ihn erst, wenn ein Wert
    dasteht; ohne Wert gibt es also gar keinen Fuß. Der Griff sagt das, nicht
    der Einbauort — dieselbe Regel wie am Tag-Kasten.

    Kein `onClose`: In den Details steht der Kasten offen, es gibt nichts zu
    schließen.
  */
  if (placement === 'inline') {
    if (!onRemove) return null
    return (
      <div className="when-dropdown__footer when-dropdown__footer--inline">
        <div className="context-menu-content__separator" />
        <div className="when-dropdown__footer-actions">
          <Button variant="outline" icon={<Trash2 size={16} />} onClick={onRemove}>
            {t('taskCreation.whenRemove')}
          </Button>
        </div>
      </div>
    )
  }

  if (!hasSelection && placement !== 'badge' && !onRemove) return null

  /*
    **Der Papierkorb kommt mit dem Griff**, an der Marke wie im Aufgaben-Menü (`actionbar`, #216 —
    dort fehlte er, der Wert ließ sich nicht mehr loswerden). Der Aufrufer reicht `onRemove` nur,
    wenn ein Wert dasteht. Er trägt „Entfernen“ wie der Dauer-Wähler (#242) – das Icon allein
    sagte nicht, was er tut.

    **Er steht direkt neben „Bestätigen“, beide rechts** (Sven, 10.10.2026) — auch allein, sonst
    spränge er nach rechts, sobald etwas gewählt ist.
  */
  return (
    <div className="when-dropdown__footer">
      <div className="context-menu-content__separator" />
      <div className="when-dropdown__footer-actions">
        {onRemove && (
          <Button
            variant="outline"
            icon={<Trash2 size={16} />}
            onClick={() => { onClose(); setTimeout(() => onRemove(), 0) }}
          >
            {t('taskCreation.whenRemove')}
          </Button>
        )}
        {hasSelection && (
          <Button
            variant="primary"
            shortcut={{ kind: 'return' }}
            onClick={onConfirm}
          >
            {t('taskCreation.whenConfirm')}
          </Button>
        )}
      </div>
    </div>
  )
}
